import { useState } from "react";
import { Type, ChevronDown } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FONT_LIBRARY, FONT_WEIGHTS } from "@/lib/fonts";

/**
 * Per-section typography overrides. Each control is *optional* — empty means
 * "inherit the global default" (which itself falls back to the theme).
 *
 * Shape:
 *   { heading_size, heading_color, body_color, font_style, text_align }
 */
export const TYPO_SIZES = [
  { id: "_default", label: "Default" },
  { id: "sm", label: "Small" },
  { id: "md", label: "Medium" },
  { id: "lg", label: "Large" },
  { id: "xl", label: "Extra large" },
];
export const TYPO_FONTS = [
  { id: "_default", label: "Default" },
  { id: "sans", label: "Sans (modern)" },
  { id: "serif", label: "Serif (elegant)" },
  { id: "display", label: "Display (bold)" },
];
export const TYPO_ALIGN = [
  { id: "_default", label: "Default" },
  { id: "left", label: "Left" },
  { id: "center", label: "Center" },
  { id: "right", label: "Right" },
];

// Build {label, id} options for the Google-Font picker, grouped by category.
const FONT_OPTIONS = [
  { id: "_default", label: "Default" },
  ...FONT_LIBRARY.map((f) => ({ id: f.id, label: `${f.label} · ${f.category}` })),
];
const WEIGHT_OPTIONS = [
  { id: "_default", label: "Default" },
  ...FONT_WEIGHTS.map((w) => ({ id: String(w.id), label: w.label })),
];

// Shadcn Select can't use empty string as a value, so we use a sentinel "_default"
// and round-trip it to undefined when calling onChange.
const SENTINEL = "_default";
const toExport = (v) => (v === SENTINEL || v === "" ? undefined : v);
const fromValue = (v) => (v ? v : SENTINEL);

export default function TypographyControls({ value = {}, onChange, scopeLabel = "Section", testIdPrefix = "typo" }) {
  const [open, setOpen] = useState(false);
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: toExport(val) });
  const hasOverrides = ["heading_size", "heading_color", "body_color", "font_style", "text_align"].some((k) => !!v[k]);

  return (
    <div className="border-2 border-dashed border-black/15 rounded-lg p-3 bg-[#FAFAF7]" data-testid={`${testIdPrefix}-panel`}>
      <button type="button" onClick={() => setOpen((x) => !x)}
        className="w-full flex items-center justify-between text-xs font-extrabold uppercase tracking-wider text-gray-700"
        data-testid={`${testIdPrefix}-toggle`}>
        <span className="flex items-center gap-2">
          <Type className="w-3.5 h-3.5" /> {scopeLabel} typography
          {hasOverrides && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#8B5CF6] text-white text-[9px]">CUSTOM</span>}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-3 text-xs" data-testid={`${testIdPrefix}-controls`}>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <ShadSelect label="Heading size" value={v.heading_size} onChange={(x) => set("heading_size", x)} options={TYPO_SIZES} testId={`${testIdPrefix}-heading-size`} />
            <ShadSelect label="Text align" value={v.text_align} onChange={(x) => set("text_align", x)} options={TYPO_ALIGN} testId={`${testIdPrefix}-align`} />
            <ShadSelect label="Style preset" value={v.font_style} onChange={(x) => set("font_style", x)} options={TYPO_FONTS} testId={`${testIdPrefix}-font-style`} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <ShadSelect label="Heading font" value={v.heading_font} onChange={(x) => set("heading_font", x)} options={FONT_OPTIONS} testId={`${testIdPrefix}-heading-font`} />
            <ShadSelect label="Body font" value={v.body_font} onChange={(x) => set("body_font", x)} options={FONT_OPTIONS} testId={`${testIdPrefix}-body-font`} />
            <ShadSelect label="Button font" value={v.button_font} onChange={(x) => set("button_font", x)} options={FONT_OPTIONS} testId={`${testIdPrefix}-button-font`} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <ShadSelect label="Heading weight" value={v.heading_weight ? String(v.heading_weight) : ""} onChange={(x) => set("heading_weight", x ? Number(x) : undefined)} options={WEIGHT_OPTIONS} testId={`${testIdPrefix}-heading-weight`} />
            <ShadSelect label="Body weight" value={v.body_weight ? String(v.body_weight) : ""} onChange={(x) => set("body_weight", x ? Number(x) : undefined)} options={WEIGHT_OPTIONS} testId={`${testIdPrefix}-body-weight`} />
            <div className="flex items-end">
              <button type="button" onClick={() => onChange({})}
                className="text-[11px] font-bold underline text-gray-600"
                data-testid={`${testIdPrefix}-reset`}>Reset to default</button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-2 gap-3">
            <ColorPick label="Heading color" value={v.heading_color} onChange={(x) => set("heading_color", x)} testId={`${testIdPrefix}-heading-color`} />
            <ColorPick label="Body color" value={v.body_color} onChange={(x) => set("body_color", x)} testId={`${testIdPrefix}-body-color`} />
          </div>
        </div>
      )}
    </div>
  );
}

function ShadSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase font-bold text-gray-600 mb-1">{label}</div>
      <Select value={fromValue(value)} onValueChange={onChange}>
        <SelectTrigger className="w-full h-9 text-xs border-2 border-black bg-white" data-testid={testId}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.id} value={o.id} className="text-xs">{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </label>
  );
}

/**
 * Color swatch with explicit "default" empty state.
 *
 * - Unset value: shows a subtle checkered / outlined swatch + the literal label
 *   "Default", and the hidden color input picks #111111 only as the *initial*
 *   value when user opens the picker — but the visual swatch never lies about
 *   the current state.
 * - Set value: swatch is the actual color.
 */
function ColorPick({ label, value, onChange, testId }) {
  const isSet = !!value;
  return (
    <label className="block">
      <div className="text-[10px] uppercase font-bold text-gray-600 mb-1">{label}</div>
      <div className="flex items-center gap-1.5">
        <span className="relative inline-block">
          <input
            type="color"
            value={value || "#111111"}
            onChange={(e) => onChange(e.target.value)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            data-testid={testId}
            aria-label={label}
          />
          <span
            className="block w-8 h-8 border-2 border-black rounded cursor-pointer"
            style={
              isSet
                ? { background: value }
                : {
                    background:
                      "repeating-conic-gradient(#E5E7EB 0% 25%, #F8FAFC 0% 50%) 0 0 / 8px 8px",
                  }
            }
            aria-hidden="true"
          />
        </span>
        {isSet ? (
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="flex-1 px-2 py-1.5 border-2 border-black rounded-md bg-white text-xs"
          />
        ) : (
          <div className="flex-1 px-2 py-1.5 border-2 border-black/30 rounded-md bg-white text-xs italic text-gray-500">
            Default
          </div>
        )}
        {isSet && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="text-[10px] font-bold underline text-gray-600"
            aria-label={`Reset ${label} to default`}
          >
            ×
          </button>
        )}
      </div>
    </label>
  );
}
